import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  useCreateDeliveryEvent,
  useUpdateDeliveryEvent,
  type ProgramItem,
  type ProgramDeliveryEvent,
} from "@/lib/program-registration";
import { CheckCircle2, Loader2 } from "lucide-react";
import { format } from "date-fns";

// Four statuses shown in the quick-log — must match Program Delivery Log wording.
const QUICK_STATUSES = [
  { key: "delivered_as_planned",     label: "Completed as planned" },
  { key: "partially_delivered",      label: "Partially completed" },
  { key: "modified_during_delivery", label: "Modified during delivery" },
  { key: "postponed",                label: "Not delivered / postponed" },
] as const;

type QuickStatusKey = typeof QUICK_STATUSES[number]["key"];

export interface PulledItem {
  registerId: number;
  item: ProgramItem;
  existingEvent: ProgramDeliveryEvent | null;
  className?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  pulledItems: PulledItem[];
  /**
   * When the dialog is opened from a diary slot that has been saved to the
   * database, pass the diary entry id so the delivery event is linked to that
   * slot with sourceType "diary_entry".
   *
   * Pass null for the legacy "manual_entry" path.
   */
  diaryEntryId: number | null;
  /** Label of the timetable slot (e.g. "Period 1") — stored as `period` on new events. */
  slotLabel: string;
  /** Class id from the timetable slot — stored as `classId` on new events so the Delivery Log can resolve the class name. */
  slotClassId?: number | null;
}

function itemLabel(pi: PulledItem): string {
  return pi.item.title || `Item ${pi.item.id}`;
}

// Inner component so hooks always close over the correct selectedPulled IDs.
function QuickLogInner({
  pulledItems,
  onClose,
  diaryEntryId,
  slotLabel,
  slotClassId,
}: {
  pulledItems: PulledItem[];
  onClose: () => void;
  diaryEntryId: number | null;
  slotLabel: string;
  slotClassId?: number | null;
}) {
  const { toast } = useToast();

  const startOnStatus = pulledItems.length === 1;
  const [step, setStep] = useState<"pick-item" | "pick-status">(
    startOnStatus ? "pick-status" : "pick-item"
  );
  const [selectedPulled, setSelectedPulled] = useState<PulledItem | null>(
    startOnStatus ? pulledItems[0] : null
  );
  const [selectedStatus, setSelectedStatus] = useState<QuickStatusKey | null>(null);
  const [reflectionNote, setReflectionNote] = useState("");
  const [reflectionTouched, setReflectionTouched] = useState(false);

  // Pre-populate status + reflection when an existing event is loaded.
  useEffect(() => {
    if (!selectedPulled?.existingEvent) return;
    const ev = selectedPulled.existingEvent;
    const matched = QUICK_STATUSES.find(s => s.key === ev.deliveryStatus);
    if (matched) setSelectedStatus(matched.key);
    setReflectionNote(ev.reflectionNote ?? "");
    setReflectionTouched(false);
  }, [selectedPulled]);

  const isEditing = Boolean(selectedPulled?.existingEvent);

  // Mirror the Program Rego rule: any status other than "Completed as planned"
  // requires an explanation so the Delivery Log export remains consistent.
  const needsReflection =
    selectedStatus !== null && selectedStatus !== "delivered_as_planned";
  const reflectionMissing = needsReflection && !reflectionNote.trim();

  const createEvent = useCreateDeliveryEvent(
    selectedPulled?.registerId ?? 0,
    selectedPulled?.item.id ?? 0
  );
  const updateEvent = useUpdateDeliveryEvent(
    selectedPulled?.registerId ?? 0,
    selectedPulled?.existingEvent?.id ?? 0,
    diaryEntryId,
  );

  const isPending = createEvent.isPending || updateEvent.isPending;

  async function handleSave() {
    if (!selectedPulled || !selectedStatus) return;

    if (needsReflection && !reflectionNote.trim()) {
      setReflectionTouched(true);
      return;
    }

    const today = format(new Date(), "yyyy-MM-dd");

    try {
      if (isEditing) {
        await updateEvent.mutateAsync({
          deliveryStatus: selectedStatus,
          reflectionNote: reflectionNote.trim() || null,
        });
      } else {
        await createEvent.mutateAsync(
          diaryEntryId !== null
            ? {
                sourceType: "diary_entry" as const,
                diaryEntryId,
                classId: slotClassId ?? null,
                deliveryStatus: selectedStatus,
                deliveryDate: today,
                period: slotLabel || null,
                reflectionNote: reflectionNote.trim() || null,
              }
            : {
                sourceType: "manual_entry" as const,
                deliveryStatus: selectedStatus,
                deliveryDate: today,
                period: slotLabel || null,
                reflectionNote: reflectionNote.trim() || null,
              }
        );
      }
      toast({ title: isEditing ? "Delivery status updated" : "Delivery status logged" });
      onClose();
    } catch (err) {
      toast({
        title: isEditing ? "Couldn't update delivery status" : "Couldn't save delivery status",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  if (step === "pick-item") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Which program item are you logging delivery for?
        </p>
        <div className="space-y-2">
          {pulledItems.map((pi, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setSelectedPulled(pi);
                setSelectedStatus(null);
                setReflectionNote("");
                setReflectionTouched(false);
                setStep("pick-status");
              }}
              className="w-full text-left px-3 py-2.5 rounded-lg border border-input bg-background hover:bg-muted/50 text-sm font-medium transition-colors flex items-center justify-between gap-2"
            >
              <span className="truncate">{itemLabel(pi)}</span>
              {pi.existingEvent && (
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
              )}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isEditing && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">
          This item already has a delivery log for this slot — updating it below.
        </p>
      )}
      <p className="text-sm text-gray-600">
        Logging delivery for:{" "}
        <span className="font-medium text-gray-900">
          {selectedPulled ? itemLabel(selectedPulled) : ""}
        </span>
      </p>
      {pulledItems.length > 1 && (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => {
            setSelectedStatus(null);
            setReflectionNote("");
            setReflectionTouched(false);
            setStep("pick-item");
          }}
        >
          ← Choose a different item
        </button>
      )}
      <div className="grid grid-cols-1 gap-2">
        {QUICK_STATUSES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setSelectedStatus(key);
              setReflectionTouched(false);
            }}
            className={`text-left px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
              selectedStatus === key
                ? "border-primary bg-primary/5 text-primary"
                : "border-input bg-background text-foreground hover:bg-muted/50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Explanation — required for any status other than "Completed as planned" */}
      {needsReflection && (
        <div className="space-y-1.5">
          <Label htmlFor="quick-reflection">
            Brief explanation <span className="text-destructive">*</span>
          </Label>
          <p className="text-xs text-muted-foreground -mt-0.5">
            Add what changed, what was not completed, or what needs follow-up.
          </p>
          <Textarea
            id="quick-reflection"
            rows={3}
            value={reflectionNote}
            onChange={(e) => setReflectionNote(e.target.value)}
            onBlur={() => setReflectionTouched(true)}
            className={reflectionTouched && reflectionMissing ? "border-destructive" : ""}
          />
          {reflectionTouched && reflectionMissing && (
            <p className="text-xs text-destructive">
              An explanation is required for this status.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isPending}
          className="gap-1.5"
          data-write-action
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5" />
          )}
          {isEditing ? "Update status" : "Save status"}
        </Button>
      </div>
    </div>
  );
}

export function DeliveryStatusQuickLog({
  open,
  onClose,
  pulledItems,
  diaryEntryId,
  slotLabel,
  slotClassId,
}: Props) {
  // Remount the inner component each time the dialog opens to reset all state.
  const [mountKey, setMountKey] = useState(0);
  useEffect(() => {
    if (open) setMountKey((k) => k + 1);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {pulledItems.some(p => p.existingEvent) ? "Update delivery status" : "Log delivery status"}
          </DialogTitle>
        </DialogHeader>
        <QuickLogInner
          key={mountKey}
          pulledItems={pulledItems}
          onClose={onClose}
          diaryEntryId={diaryEntryId}
          slotLabel={slotLabel}
          slotClassId={slotClassId}
        />
      </DialogContent>
    </Dialog>
  );
}
